'use client';

import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';

import { DownArrowIcon, UpArrowIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { useBreakpoint } from '@contexts/device';

import {
  AccordionWrapper,
  AccordionContent,
  AccordionLabel,
  AccordionHeader,
  CustomIconWrapper,
} from './styled';
import { AccordionProps } from './props';

const Accordion = ({
  children,
  label,
  headerFullBorder = false,
  borderRadius,
  accordionContentBorderRadius,
  isStaticPage,
  borderColor = 'lightBlueGrey',
  headerPadding = '10px',
  backgroundColor,
  headerContentColor = 'white',
  bodyBackgroundColor,
  iconColor = 'white',
  defaultOpen = false,
  width = '100%',
  iconSize = 12,
  labelFontSize = '18px',
  mobileLabelFontSize = '14px',
  openedIcon = UpArrowIcon,
  closedIcon = DownArrowIcon,
  contentPadding = '10px',
  alwaysShowBottomBorder = true,
  showBottomBorderOnOpened = false,
  showBottomBorderOnClosed = false,
  showBodyBorder = false,
  differentColorWhenOpened = false,
  openedColor = 'white',
  labelWeight,
  showIconOnTop = false,
  closeOnOutsideClick = false,
  selectionCompleted = false,
  responsivePadding,
  itemCategory,
  filterHeight,
  href,
  accordionTitleStyles,
  accordionWrapperStyles,
}: AccordionProps) => {
  const accordionRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  useEffect(() => {
    setIsOpen(false);
  }, [selectionCompleted]);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        accordionRef.current &&
        !accordionRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (closeOnOutsideClick && isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [closeOnOutsideClick, isOpen]);
  const { xl } = useBreakpoint();
  const shouldShowBottom =
    alwaysShowBottomBorder ||
    (showBottomBorderOnOpened && isOpen) ||
    (showBottomBorderOnClosed && !isOpen);
  return (
    <AccordionWrapper
      width={width}
      ref={accordionRef}
      style={accordionWrapperStyles}
    >
      <AccordionHeader
        data-testid='accordion-header'
        showIconOnTop={showIconOnTop}
        filterHeight={filterHeight}
        headerFullBorder={headerFullBorder}
        alwaysShowBottomBorder={shouldShowBottom}
        borderRadius={
          isStaticPage ? '8px' : isOpen && xl ? '8px 8px 0px 0px' : borderRadius
        }
        headerPadding={headerPadding}
        backgroundColor={
          differentColorWhenOpened && isOpen ? openedColor : backgroundColor
        }
        headerContentColor={headerContentColor}
        borderColor={borderColor}
        onClick={toggleAccordion}
        responsivePadding={responsivePadding}
      >
        {itemCategory ? (
          <Link
            href={itemCategory?.href as string}
            title={itemCategory?.title}
            prefetch={false}
          >
            <AccordionLabel
              title={itemCategory?.title}
              labelWeight={labelWeight}
              labelFontSize={labelFontSize}
              mobileLabelFontSize={mobileLabelFontSize}
              style={accordionTitleStyles}
            >
              {label}
            </AccordionLabel>
          </Link>
        ) : href ? (
          <Link
            href={href ?? ''}
            title={typeof label === 'string' ? label : undefined}
            prefetch={false}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <AccordionLabel
              title={typeof label === 'string' ? label : undefined}
              labelWeight={labelWeight}
              labelFontSize={labelFontSize}
              mobileLabelFontSize={mobileLabelFontSize}
              style={{ color: headerContentColor }}
            >
              {label}
            </AccordionLabel>
          </Link>
        ) : (
          <AccordionLabel
            title={typeof label === 'string' ? label : undefined}
            labelWeight={labelWeight}
            labelFontSize={labelFontSize}
            mobileLabelFontSize={mobileLabelFontSize}
            style={{ color: headerContentColor }}
          >
            {label}
          </AccordionLabel>
        )}

        <CustomIconWrapper>
          <CustomIcon
            icon={isOpen ? openedIcon : closedIcon}
            fontSize={iconSize}
            paletteColor={iconColor}
          />
        </CustomIconWrapper>
      </AccordionHeader>
      {isOpen ? (
        <AccordionContent
          backgroundColor={
            bodyBackgroundColor
              ? differentColorWhenOpened
                ? isOpen
                  ? openedColor
                  : bodyBackgroundColor
                : bodyBackgroundColor
              : differentColorWhenOpened
              ? isOpen
                ? openedColor
                : backgroundColor
              : backgroundColor
          }
          contentPadding={contentPadding}
          isOpen={isOpen}
          showBodyBorder={showBodyBorder}
          borderColor={borderColor}
          borderRadius={
            accordionContentBorderRadius
              ? '0'
              : isOpen && xl
              ? '0px 0px 8px 8px'
              : borderRadius
          }
        >
          {children}
        </AccordionContent>
      ) : null}
    </AccordionWrapper>
  );
};

export default Accordion;
